<template>
    <div :class="{ 'contact-us-logged-in-container': isAuthenticated }">
        <a
            :href="isAuthenticated ? '#' : 'mailto:help@ipacconsulting.com'"
            class="contact-button"
            @click="handleContactClick"
        >
            <span class="contact-text-box-wrapper">
                <Transition name="contact-label" appear>
                    <span
                        v-if="showFullLabel"
                        key="full"
                        class="contact-text-box contact-text-box--full"
                    >
                        Hello, do you have any questions?
                        <span class="contact-subtext"
                            >Please contact us anytime!</span
                        >
                    </span>
                    <span
                        v-else
                        key="short"
                        class="contact-text-box contact-text-box--short"
                    >
                        Contact Us
                    </span>
                </Transition>
            </span>
            <img
                class="contact-patty"
                src="@/assets/images/patty-clipped.svg"
                alt=""
            />
        </a>

        <SidePanel
            v-if="isAuthenticated"
            :visible="isPanelVisible"
            title="Email Customer Service"
            size="panel-md"
            @close="closePanel"
        >
            <RequestForm
                :user="user"
                :success="formSuccess"
                requestType="email"
                @submit="handleFormSubmission"
            />
        </SidePanel>
    </div>
</template>

<script>
import SidePanel from './SidePanel.vue';
import RequestForm from './contact_us/RequestForm.vue';

export default {
    name: 'ContactUsButton',
    components: {
        SidePanel,
        RequestForm,
    },
    data() {
        return {
            isPanelVisible: false,
            formSuccess: false,
            showFullLabel: true,
            labelTimer: null,
        };
    },
    mounted() {
        this.labelTimer = setTimeout(() => {
            this.showFullLabel = false;
        }, 4000);
    },
    beforeUnmount() {
        if (this.labelTimer) clearTimeout(this.labelTimer);
    },
    computed: {
        isAuthenticated() {
            return this.$userStore?.isAuthenticated || false;
        },
        // User data from store
        user() {
            const user = this.$userStore.user || {};
            return {
                name: this.$userStore.getFullName || '',
                email: user.email || '',
                phone: user.phone || '',
                company: user.company?.name || '',
            };
        },
    },
    methods: {
        handleContactClick(e) {
            if (this.isAuthenticated) {
                e.preventDefault();
                this.showEmailForm();
            }
        },
        showEmailForm() {
            this.isPanelVisible = true;
            this.formSuccess = false;
        },
        closePanel() {
            this.isPanelVisible = false;
            this.formSuccess = false;
        },
        handleFormSubmission(formData) {
            const endpoint = 'support-ticket/create';
            const successMessage = 'Message sent successfully';

            this.$postData(endpoint, formData).then((response) => {
                if (response.success) {
                    this.$alertStore.pushSuccess(successMessage);
                    this.formSuccess = true;
                } else if (response.message) {
                    this.$alertStore.pushError(response.message);
                }
            });
        },
    },
};
</script>

<style scoped>
/* ============================================
   CONTACT BUTTON
   ============================================ */

/* Register animatable CSS variables */
@property --contact-opacity {
    syntax: '<number>';
    inherits: false;
    initial-value: 0.6;
}

@property --contact-y {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 20%;
}

/* Main animation timeline: fade up (1s) → visible (3s) → fade down (1s) */
@keyframes contactReveal {
    0% {
        --contact-opacity: 0.6;
        --contact-y: 20%;
    }
    20% {
        /* 1s - show full text */
        --contact-opacity: 1;
        --contact-y: 0%;
    }
    80% {
        /* 4s - still showing full text */
        --contact-opacity: 1;
        --contact-y: 0%;
    }
    100% {
        /* 5s - fade down */
        --contact-opacity: 0.7;
        --contact-y: 20%;
    }
}

/* Enable pointer events after animation completes */
@keyframes enablePointer {
    to {
        pointer-events: auto;
    }
}

/* Main contact button */
.contact-button {
    position: fixed;
    right: 0.625rem;
    bottom: 0;
    z-index: var(--z-index-floating);
    width: fit-content;
    height: 5.3125rem;
    padding: 0;
    text-decoration: none;
    pointer-events: none;

    /* Apply animated variables */
    opacity: var(--contact-opacity);
    translate: 0 var(--contact-y);

    /* Run animations */
    animation: contactReveal 5s ease forwards, enablePointer 0s 5s forwards;

    /* Smooth transitions on hover */
    transition: opacity 200ms ease, translate 200ms ease;
    will-change: opacity, translate;

    &:hover {
        opacity: 1;
        translate: 0 0;
    }
}

/* Wrapper so transition children stack in same position */
.contact-text-box-wrapper {
    position: absolute;
    bottom: 0.5rem;
    right: 5rem;
    left: auto;
    width: 12rem;
}

/* Label transition: 1s fade to match original (initial appear + full→short crossfade) */
.contact-label-enter-active,
.contact-label-leave-active {
    transition: opacity 1s ease;
}
.contact-label-enter-from,
.contact-label-leave-to {
    opacity: 0;
}

/* Text box with speech bubble (position relative to wrapper) */
.contact-text-box {
    position: absolute;
    bottom: 0;
    right: 0;
    left: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: var(--space-1);
    width: 12rem;
    padding: var(--space-2);
    border-radius: var(--radius-default);
    border: 1px solid #e6e6e6;
    background: var(--white);
    font-size: 0.875rem;
    line-height: 1.1;
    color: var(--neutral-900);
    text-align: center;
    text-wrap: balance;

    transition: opacity 150ms ease;
    will-change: opacity;

    &:after {
        content: '';
        position: absolute;
        right: -0.625rem;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 6px 0 6px 10.4px;
        border-color: transparent transparent transparent var(--neutral-700);
    }
}

/* Short version - shows after transition (1rem higher than full, relative to wrapper) */
.contact-text-box--short {
    width: 7rem;
    bottom: 1rem;
}

/* Subtext styling */
.contact-subtext {
    font-size: 0.8125rem;
    font-weight: 400;
    color: var(--neutral-800);
}

/* Patty image */
.contact-patty {
    position: absolute;
    right: 0;
    bottom: 0;
    will-change: opacity, translate;
}
</style>
